import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import Breadcrumb from "@/components/base/Breadcrumb";
import Pagination from "@/components/base/Pagination";
import { getArticlesByType } from "@/utils/data";

const GUIDE_PAGE_SIZE = 4;

function ArticleRow({
  cover,
  tag,
  date,
  title,
  excerpt,
}: {
  cover: string;
  tag: string;
  date: string;
  title: string;
  excerpt: string;
}) {
  return (
    <article className="group flex flex-col sm:flex-row gap-4 md:gap-6 rounded-lg border border-background-200 bg-background-50 overflow-hidden hover:border-primary-300 transition-colors">
      <div className="w-full sm:w-56 md:w-64 h-44 sm:h-40 shrink-0 overflow-hidden bg-background-200">
        <img
          src={cover}
          alt={title}
          title={`${title} - 观影指南`}
          className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="flex-1 min-w-0 p-4 md:py-5 md:pr-6">
        <div className="flex items-center gap-2 mb-2">
          <span className="px-2 py-0.5 rounded-md bg-accent-100 text-accent-700 text-xs">{tag}</span>
          <span className="text-xs text-foreground-400">{date}</span>
        </div>
        <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors">
          {title}
        </h3>
        <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2">{excerpt}</p>
        <details className="mt-3 group/details">
          <summary className="inline-flex items-center gap-1.5 text-sm text-primary-600 cursor-pointer select-none list-none">
            阅读全文
            <i className="ri-arrow-down-s-line group-open/details:rotate-180 transition-transform"></i>
          </summary>
          <p className="mt-3 text-sm text-foreground-700 leading-loose whitespace-pre-line">
            {excerpt}
          </p>
        </details>
      </div>
    </article>
  );
}

export default function GuidesPage() {
  useSeo({
    title: "观影指南及资讯 - 蜜桃成熟时1997影视内容站",
    description:
      "提供经典电影观影建议、影视史背景讲解与最新影视资讯，帮助你更好地了解九十年代亚洲电影，找到适合自己的观影顺序与方式。",
    keywords: "观影指南,影视资讯,影视史,电影文化,九十年代电影,蜜桃成熟时1997",
  });

  const [page, setPage] = useState(1);
  const [newsCount, setNewsCount] = useState(4);

  const guides = useMemo(() => getArticlesByType("guide"), []);
  const news = useMemo(() => getArticlesByType("news"), []);

  const totalPages = Math.max(1, Math.ceil(guides.length / GUIDE_PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedGuides = guides.slice(
    (currentPage - 1) * GUIDE_PAGE_SIZE,
    currentPage * GUIDE_PAGE_SIZE,
  );
  const visibleNews = news.slice(0, newsCount);

  return (
    <div className="w-full">
      <div className="w-full px-4 md:px-8 pt-8 md:pt-10">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "观影指南" }]} />
      </div>

      <section className="w-full px-4 md:px-8 pt-6 pb-10 md:pb-12">
        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium">
          <i className="ri-compass-3-line"></i>
          观影指南
        </span>
        <h1 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-foreground-950">
          观影指南及资讯
        </h1>
        <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-3xl leading-relaxed">
          无论是第一次接触九十年代电影，还是想系统了解这段影史，这里有观影建议、影视史背景与电影文化讲解，以及最新的影视资讯动态，帮你更从容地走进这些经典作品。
        </p>
      </section>

      <section className="w-full px-4 md:px-8 pb-12">
        <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-6">
          指南文章列表
        </h2>
        <div className="space-y-4 md:space-y-5">
          {pagedGuides.map((item) => (
            <ArticleRow
              key={item.id}
              cover={item.cover}
              tag={item.tag}
              date={item.date}
              title={item.title}
              excerpt={item.content}
            />
          ))}
        </div>
        <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
      </section>

      <section className="w-full px-4 md:px-8 py-12 md:py-16 bg-background-100">
        <div className="flex items-center gap-2 mb-6">
          <span className="w-9 h-9 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center">
            <i className="ri-newspaper-line"></i>
          </span>
          <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950">
            最新影视资讯
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {visibleNews.map((item) => (
            <article
              key={item.id}
              className="rounded-lg border border-background-200 bg-background-50 p-5"
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-0.5 rounded-md bg-secondary-100 text-secondary-900 text-xs">
                  {item.tag}
                </span>
                <span className="text-xs text-foreground-400">{item.date}</span>
              </div>
              <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-950">
                {item.title}
              </h3>
              <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{item.excerpt}</p>
              <details className="mt-3 group/details">
                <summary className="inline-flex items-center gap-1.5 text-sm text-primary-600 cursor-pointer select-none list-none">
                  展开详情
                  <i className="ri-arrow-down-s-line group-open/details:rotate-180 transition-transform"></i>
                </summary>
                <p className="mt-3 text-sm text-foreground-700 leading-loose whitespace-pre-line">
                  {item.content}
                </p>
              </details>
            </article>
          ))}
        </div>

        {newsCount < news.length ? (
          <div className="text-center mt-8">
            <button
              type="button"
              onClick={() => setNewsCount((prev) => prev + 3)}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-background-300 text-sm text-foreground-800 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line"></i>
              加载更多资讯
            </button>
          </div>
        ) : (
          <p className="text-center text-xs text-foreground-400 mt-8">已显示全部资讯</p>
        )}
      </section>

      <section className="w-full px-4 md:px-8 py-10 md:py-14">
        <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8 text-center">
          <p className="text-sm text-foreground-600">
            想直接看影片资料？前往
            <Link to="/category" className="text-primary-600 hover:underline mx-1">
              电影分类
            </Link>
            或浏览
            <Link to="/features/plot-analysis" className="text-primary-600 hover:underline mx-1">
              剧情解析专题
            </Link>
            。
          </p>
        </div>
      </section>
    </div>
  );
}